import { StButton } from "@storytell/ui/buttons";
import { TbX } from "solid-icons/tb";
import { type Component, For, Show } from "solid-js";
import { TagInput } from "./TagInput";
import type { DomainInvite, Role } from "./types";

interface CreateDomainInviteFormProps {
  newDomainInvites: DomainInvite[];
  pendingDomains: string[];
  currentDomainInput: string;
  selectedRole: Role;
  errorMessage: string;
  availableRoles?: Role[]; // Optional: if provided, only show these roles in dropdown
  onPendingDomainsChange: (domains: string[]) => void;
  onCurrentInputChange: (input: string) => void;
  onRoleChange: (role: Role) => void;
  onAddDomains: () => void;
  onRemoveDomain: (id: string) => void;
  onUpdateDomainRole: (id: string, role: Role) => void;
  onSubmit: () => void;
  onClose: () => void;
}

const DOMAIN_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.([a-zA-Z]{2,}|[a-zA-Z]{2,}\.[a-zA-Z]{2,})$/;

export const CreateDomainInviteForm: Component<CreateDomainInviteFormProps> = (props) => {
  const isAddButtonDisabled = () => {
    const hasValidPending =
      props.pendingDomains.length > 0 && props.pendingDomains.every((domain) => DOMAIN_REGEX.test(domain.trim()));
    const hasValidCurrent = props.currentDomainInput.trim() && DOMAIN_REGEX.test(props.currentDomainInput.trim());
    return !hasValidPending && !hasValidCurrent;
  };

  return (
    <div class="p-4 bg-canvas border border-primary-border rounded-lg space-y-3">
      <div class="flex items-center justify-between">
        <h4 class="text-sm font-medium">Add Domain Access</h4>
        <StButton onClick={props.onClose} class="p-1 hover:bg-canvas-hover rounded">
          <TbX class="w-4 h-4" />
        </StButton>
      </div>
      <p class="text-sm text-muted-text mb-6">
        Anyone with an email from this domain will see the project
        <br />
        Separate multiple domains with a comma
      </p>

      <Show when={props.newDomainInvites.length > 0}>
        <div class="space-y-2">
          <For each={props.newDomainInvites}>
            {(invite) => (
              <div class="flex items-center gap-2 p-2 bg-panel rounded-lg border border-primary-border">
                <span class="text-sm flex-1 truncate">@{invite.domain}</span>
                <select
                  value={invite.role}
                  onChange={(e) => props.onUpdateDomainRole(invite.id, e.currentTarget.value as Role)}
                  class="px-2 py-1 text-xs bg-canvas border border-primary-border rounded focus:outline-none focus:ring-2 focus:ring-brand"
                  disabled={props.availableRoles && props.availableRoles.length === 1}
                >
                  <For each={props.availableRoles || ["Owner", "Collaborator", "Reader"]}>
                    {(role) => <option value={role}>{role}</option>}
                  </For>
                </select>
                <button
                  type="button"
                  onClick={() => props.onRemoveDomain(invite.id)}
                  class="p-1 hover:bg-canvas-hover rounded transition-colors"
                >
                  <TbX class="w-4 h-4 text-muted-text" />
                </button>
              </div>
            )}
          </For>
        </div>
      </Show>

      {/* Domain error message */}
      <Show when={props.errorMessage}>
        <div class="p-3 bg-red-500/10 border border-red-500/20 rounded-lg mb-3">
          <p class="text-sm text-red-500">{props.errorMessage}</p>
        </div>
      </Show>

      <div class="flex gap-2">
        <TagInput
          placeholder="MyCompany.com, MyClient.com, etc..."
          value={props.pendingDomains}
          onValueChange={props.onPendingDomainsChange}
          onInputChange={props.onCurrentInputChange}
          onAdd={async () => {
            // Check if we have pending domains before processing
            const hadPendingDomains = props.pendingDomains.length > 0;
            // Process the pending domains (this will add them to newDomainInvites)
            props.onAddDomains();
            // Submit if we had pending domains (meaning domains were processed)
            if (hadPendingDomains) {
              // Use a small delay to ensure state updates are complete
              setTimeout(async () => {
                await props.onSubmit();
              }, 50);
            }
          }}
          validator={(domain) => DOMAIN_REGEX.test(domain.trim())}
          class="flex-1"
        />
        <select
          value={props.selectedRole}
          onChange={(e) => props.onRoleChange(e.currentTarget.value as Role)}
          class="px-3 py-2 bg-panel border border-primary-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          disabled={props.availableRoles && props.availableRoles.length === 1}
        >
          <For each={props.availableRoles || ["Owner", "Collaborator", "Reader"]}>
            {(role) => <option value={role}>{role}</option>}
          </For>
        </select>
        <button
          type="button"
          onClick={async () => {
            props.onAddDomains();
            await props.onSubmit();
          }}
          disabled={isAddButtonDisabled()}
          class="px-4 py-2 bg-brand text-on-brand hover:bg-brand-hover border border-primary-border rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add
        </button>
      </div>
    </div>
  );
};
